import { formatShortAbsolute } from "@istari-frontend/shared";
import { Avatar, AvatarFallback, Button } from "@istari-frontend/ui";
import { PencilIcon, RotateCwIcon, Sparkles } from "lucide-react";
import { type KeyboardEvent, lazy, type ReactNode, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import type { ChatMessage } from "~/ai-chat/model/chat-message";
import type { FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import type { MeshViewRequest } from "~/ai-chat/model/mesh-file-types";
import { ChatCopyButton } from "~/ai-chat/ui/chat-copy-button";
import { ChatMarkdown, type RenderEntityChip, type RenderSaveResourceAction } from "~/ai-chat/ui/chat-markdown";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";
import { cn } from "~/utils/cn";

// Lazy so the viewer bundles load only once a user opens a mesh or a preview.
const ChatMeshViewer = lazy(() => import("~/ai-chat/feature/chat-mesh-viewer"));
const ChatFilePreview = lazy(() => import("~/ai-chat/feature/chat-file-preview"));

// Card-frame shell for a viewer's loading and load-failure states, matching the
// viewer's own outer frame so the placeholder doesn't jump when content appears.
// heightClass matches the enclosed viewer (mesh h-64, file preview h-96).
function ViewerShell({ children, status, heightClass }: { children: ReactNode; status?: string; heightClass: string }) {
  return (
    <div
      role={status ? "status" : undefined}
      aria-label={status}
      className={cn(
        "border-border bg-muted/20 text-muted-foreground mt-1.5 flex w-full max-w-[85%] items-center justify-center rounded-lg border text-sm",
        heightClass,
      )}
    >
      {children}
    </div>
  );
}

const ASSISTANT_NAME = "Istari AI";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

interface ChatMessageBubbleProps {
  message: ChatMessage;
  // Display name for the current user's turns; assistant turns are always "Istari AI".
  userName?: string;
  isStreaming?: boolean;
  // Reword this user turn and re-ask: the transcript is truncated here and the
  // edited prompt is re-sent. Set only on user messages while idle.
  onEdit?: (content: string) => void;
  // Re-ask this turn unchanged (truncate here and re-send). Set while idle.
  onRetry?: () => void;
  renderSaveResourceAction?: RenderSaveResourceAction;
  renderEntityChip?: RenderEntityChip;
  // When set, a file's Preview opens in an external surface (the chat page's
  // canvas pane) instead of inline below the message. Absent = inline (widget).
  onOpenArtifact?: (file: FilePreviewRequest) => void;
}

export function ChatMessageBubble({
  message,
  userName,
  isStreaming,
  onEdit,
  onRetry,
  renderSaveResourceAction,
  renderEntityChip,
  onOpenArtifact,
}: ChatMessageBubbleProps) {
  const isUser = message.role === "user";
  const authorName = isUser ? (userName ?? "You") : ASSISTANT_NAME;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  // Meshes / files the user opened from chips in this message, rendered inline below it.
  const [openMeshes, setOpenMeshes] = useState<MeshViewRequest[]>([]);
  const [openPreviews, setOpenPreviews] = useState<FilePreviewRequest[]>([]);

  const openMesh = (mesh: MeshViewRequest) => {
    setOpenMeshes((prev) => (prev.some((m) => m.resourceId === mesh.resourceId) ? prev : [...prev, mesh]));
  };
  const closeMesh = (resourceId: string) => {
    setOpenMeshes((prev) => prev.filter((m) => m.resourceId !== resourceId));
  };
  const openPreview = (file: FilePreviewRequest) => {
    setOpenPreviews((prev) => (prev.some((f) => f.resourceId === file.resourceId) ? prev : [...prev, file]));
  };
  const closePreview = (resourceId: string) => {
    setOpenPreviews((prev) => prev.filter((f) => f.resourceId !== resourceId));
  };

  const avatar = isUser ? (
    <Avatar className="size-8 shrink-0">
      <AvatarFallback className="text-xs">{initials(authorName)}</AvatarFallback>
    </Avatar>
  ) : (
    <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
      <Sparkles className="text-primary size-4" />
    </div>
  );

  const header = (
    <div className="flex items-baseline gap-2">
      <span className={cn("text-sm font-medium", isUser ? "text-primary" : "text-foreground")}>{authorName}</span>
      {message.createdAt ? (
        <time dateTime={message.createdAt.toISOString()} className="text-muted-foreground text-xs">
          {formatShortAbsolute(message.createdAt)}
        </time>
      ) : null}
    </div>
  );

  if (isEditing) {
    const trimmed = draft.trim();
    const submit = () => {
      if (trimmed.length === 0) return;
      setIsEditing(false);
      onEdit?.(trimmed);
    };
    const cancel = () => {
      setIsEditing(false);
      setDraft(message.content);
    };
    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
      } else if (event.key === "Escape") {
        cancel();
      }
    };
    return (
      <div className="flex w-full gap-3">
        {avatar}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {header}
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            autoFocus
            aria-label="Edit message"
            className="bg-accent text-accent-foreground field-sizing-content max-h-40 w-full resize-none rounded-lg px-3 py-2 text-base leading-snug outline-none sm:text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="2xs" onClick={cancel}>
              Cancel
            </Button>
            <Button type="button" size="2xs" onClick={submit} disabled={trimmed.length === 0}>
              Send
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex w-full gap-3">
      {avatar}
      {/* min-w-0: a flex item's min-width:auto tracks intrinsic content width, so one long
          unbroken code line would push the column past the panel and widen the whole row. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {header}
        <div
          className={cn(
            "min-w-0 text-sm leading-snug break-words",
            isUser ? "bg-accent text-accent-foreground w-fit max-w-full rounded-lg px-3 py-2 whitespace-pre-wrap" : "",
          )}
        >
          {isUser ? (
            message.content
          ) : (
            <ChatMarkdown
              content={message.content}
              isStreaming={isStreaming}
              renderSaveResourceAction={renderSaveResourceAction}
              renderEntityChip={renderEntityChip}
              onViewMesh={openMesh}
              onPreview={onOpenArtifact ?? openPreview}
            />
          )}
        </div>
        {openMeshes.map((mesh) => (
          // Local boundary so a failed lazy-chunk load shows an inline message
          // instead of reaching the app-wide boundary and blanking the app.
          <ErrorBoundary
            key={mesh.resourceId}
            fallback={<ViewerShell heightClass="h-64">Couldn&apos;t load the 3D viewer.</ViewerShell>}
          >
            <Suspense
              fallback={
                <ViewerShell status="Loading 3D viewer" heightClass="h-64">
                  <ThemedLoadingSpinner width="24px" height="24px" />
                </ViewerShell>
              }
            >
              <ChatMeshViewer mesh={mesh} onClose={() => closeMesh(mesh.resourceId)} />
            </Suspense>
          </ErrorBoundary>
        ))}
        {openPreviews.map((file) => (
          <ErrorBoundary
            key={file.resourceId}
            fallback={<ViewerShell heightClass="h-96">Couldn&apos;t load the preview.</ViewerShell>}
          >
            <Suspense
              fallback={
                <ViewerShell status="Loading preview" heightClass="h-96">
                  <ThemedLoadingSpinner width="24px" height="24px" />
                </ViewerShell>
              }
            >
              <ChatFilePreview file={file} onClose={() => closePreview(file.resourceId)} />
            </Suspense>
          </ErrorBoundary>
        ))}
        {/* ChatGPT-style action row: revealed on hover/focus below the bubble. Height is
          reserved so revealing it doesn't shift the transcript. */}
        <div
          className={cn(
            "text-muted-foreground flex items-center gap-2 px-1 transition-opacity",
            // Hover-reveal only on hover-capable pointers; touch devices (no hover)
            // keep the row visible so Copy/Edit/timestamp stay reachable.
            "[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100",
          )}
        >
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              aria-label={isUser ? "Resend message" : "Regenerate response"}
              className="hover:text-foreground shrink-0 transition-colors"
            >
              <RotateCwIcon className="size-3" aria-hidden="true" />
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              onClick={() => {
                setDraft(message.content);
                setIsEditing(true);
              }}
              aria-label="Edit message"
              className="hover:text-foreground shrink-0 transition-colors"
            >
              <PencilIcon className="size-3" aria-hidden="true" />
            </button>
          ) : null}
          <ChatCopyButton text={message.content} copiedMessage="Message copied." ariaLabel="Copy message" />
        </div>
      </div>
    </div>
  );
}

// Processing indicator while the assistant works: an animated Istari star + a label
// ("Working" by default, or the live tool activity like "Using search…").
export function ChatMessageTypingBubble({ label }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="text-primary flex items-center gap-2 text-sm">
      <Sparkles className="size-4 motion-safe:animate-pulse" aria-hidden="true" />
      <span>{label ?? "Working"}</span>
    </div>
  );
}
